import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import Navbar from './navbar';
import Footer from './Footer';
import RippleWaveLoader from './ui/RippleWaveLoader';
import { BASE_URL } from '../apiConfig';
import { auth } from '../firebase';
import { Alert, AlertTitle } from './ui/alert';

const LOADING_MESSAGES = [
  'Fetching saved use cases…',
  'Applying your filters…',
  'Sorting results…',
];

interface UseCase {
  usecase_id: number;
  title: string;
  description: string;
  source: string[];
  domain: string;
  created_on?: string;
}

interface SearchResponse {
  status: string;
  data: {
    usecases: UseCase[];
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}


const PAGE_SIZE = 12;

const LatestUseCases = () => {
  const [useCases, setUseCases] = useState<UseCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [openSources, setOpenSources] = useState<number | null>(null);

  // Search / filter state
  const [searchText, setSearchText] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('');
  const [sortOrder, setSortOrder] = useState<'recent' | 'top'>('recent');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [domains, setDomains] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [, setHasSearched] = useState(false);

  const navigate = useNavigate();
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch domain list on mount
  useEffect(() => {
    axios.get(`${BASE_URL}/latest/usecases/domains`)
      .then(r => { if (r.data?.data) setDomains(r.data.data); })
      .catch(() => {});
  }, []);

  const fetchUseCases = useCallback(async (
    text: string,
    domain: string,
    sort: 'recent' | 'top',
    page: number,
    from: string,
    to: string,
    append = false,
  ) => {
    if (append) { setLoadingMore(true); } else { setLoading(true); }

    try {
      // Always use the search endpoint so newly saved use cases appear immediately
      // (the cached /latest/usecases sorts by composite score, burying new items)
      const params: Record<string, string | number> = { sort, page, limit: PAGE_SIZE };
      if (text.trim()) params.q = text.trim();
      if (domain) params.domain = domain;
      if (from) params.date_from = from;
      if (to) params.date_to = to;

      const res = await axios.get<SearchResponse>(`${BASE_URL}/latest/usecases/search`, { params });
      const d = res.data?.data;
      if (append) {
        setUseCases(prev => [...prev, ...(d?.usecases || [])]);
      } else {
        setUseCases(d?.usecases || []);
      }
      setTotalCount(d?.total || 0);
      setTotalPages(d?.pages || 1);
      setCurrentPage(d?.page || 1);
      setHasSearched(true);
    } catch (_err) {
      setError('Failed to fetch use cases');
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 3000);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchUseCases('', '', 'recent', 1, '', '');
  }, [fetchUseCases]);

  // Debounced re-fetch when any filter changes
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchUseCases(searchText, selectedDomain, sortOrder, 1, dateFrom, dateTo);
    }, 350);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchText, selectedDomain, sortOrder, dateFrom, dateTo, fetchUseCases]);

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchUseCases(searchText, selectedDomain, sortOrder, nextPage, dateFrom, dateTo, true);
  };

  const handleViewDetails = (useCase: UseCase) => {
    if (!auth.currentUser) {
      setError('Please login to view details');
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 3000);
      return;
    }
    navigate(`/details/${useCase.usecase_id}`, {
      state: {
        title: useCase.title,
        description: useCase.description,
        domain: useCase.domain,
        source: Array.isArray(useCase.source) ? useCase.source : (useCase.source ? [useCase.source] : []),
        usecase_id: useCase.usecase_id,
      },
    });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div
      className="min-h-screen flex flex-col overflow-x-hidden"
      style={{
        background: "radial-gradient(35% 19% at 50% 0%, rgba(255, 84, 31, 0.5) 0%, rgba(0, 0, 0, 0) 100%), black",
        color: "white"
      }}
    >
      {/* Dots Pattern */}
      <div
        className="absolute bg-dots"
        style={{
          width: "1106px", height: "500px", top: "-12px", left: "194px",
          backgroundImage: "url('/dots.png')", backgroundSize: "contain",
          backgroundRepeat: "no-repeat", opacity: 1,
          pointerEvents: "none", userSelect: "none", zIndex: 0,
        }}
      />

      {/* Error Alert */}
      {showErrorAlert && error && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md">
          <Alert>
            <AlertTitle>
              <span role="img" aria-label="error" className="mr-2">⚠️</span>
              {error}
            </AlertTitle>
          </Alert>
        </div>
      )}

      {/* Header */}
      <div className="w-full bg-transparent border-b border-white/10 shadow-sm relative z-[1000]">
        <Navbar />
      </div>

      {/* Main Content */}
      <main className="flex-1 w-full max-w-[1440px] mx-auto px-4 md:px-12 lg:px-24 py-8 mt-20">

        {/* Page Header */}
        <div className="text-center mb-10 relative">
          <button
            onClick={() => navigate("/")}
            className="absolute left-0 top-0 flex items-center gap-1.5 text-sm text-gray-400 hover:text-white px-3 py-1.5 rounded-lg hover:bg-white/10 transition-all border border-white/10"
          >
            ← Back to Chat
          </button>
          <h1 className="text-4xl font-bold text-white mb-2">Saved Use Cases</h1>
          <p className="text-gray-400 text-sm">
            Use cases saved from the discovery platform · {totalCount > 0 ? `${totalCount} total` : ''}
          </p>
        </div>

        {/* Search + Filter Bar */}
        <div className="flex flex-col md:flex-row gap-3 mb-8 max-w-4xl mx-auto">
          {/* Search input */}
          <div className="flex-1 relative">
            <svg
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500"
              width="16" height="16" fill="none" viewBox="0 0 24 24"
            >
              <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search by title or description…"
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-[#1A1A1A] border border-white/10 text-white placeholder-white/25 focus:outline-none focus:border-[#FF541F] transition-all text-sm"
            />
            {searchText && (
              <button
                onClick={() => setSearchText('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition"
              >
                ✕
              </button>
            )}
          </div>

          {/* Domain filter */}
          <select
            value={selectedDomain}
            onChange={e => setSelectedDomain(e.target.value)}
            className="px-4 py-3 rounded-xl bg-[#1A1A1A] border border-white/10 text-white focus:outline-none focus:border-[#FF541F] text-sm min-w-[160px]"
          >
            <option value="">All Domains</option>
            {domains.map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>

          {/* Sort toggle */}
          <div className="flex items-center gap-1 p-1 bg-[#1A1A1A] border border-white/10 rounded-xl">
            <button
              onClick={() => setSortOrder('recent')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                sortOrder === 'recent' ? 'bg-[#FF541F] text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              🕐 Recent
            </button>
            <button
              onClick={() => setSortOrder('top')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                sortOrder === 'top' ? 'bg-[#FF541F] text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              ⭐ Top Rated
            </button>
          </div>
        </div>

        {/* Date range filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4 max-w-4xl mx-auto">
          <div className="flex items-center gap-2 flex-1">
            <label className="text-xs text-gray-500 whitespace-nowrap">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="flex-1 px-3 py-2.5 rounded-xl bg-[#1A1A1A] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF541F] transition-all [color-scheme:dark]"
            />
          </div>
          <div className="flex items-center gap-2 flex-1">
            <label className="text-xs text-gray-500 whitespace-nowrap">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="flex-1 px-3 py-2.5 rounded-xl bg-[#1A1A1A] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF541F] transition-all [color-scheme:dark]"
            />
          </div>
          {(dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="text-xs text-gray-500 hover:text-[#FF541F] transition px-2 whitespace-nowrap self-center"
            >
              Clear dates ✕
            </button>
          )}
        </div>

        {/* Active filters indicator */}
        {(searchText || selectedDomain || dateFrom || dateTo) && (
          <div className="flex flex-wrap items-center gap-2 mb-6 max-w-4xl mx-auto">
            <span className="text-xs text-gray-500">Filtering:</span>
            {searchText && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#FF541F]/15 border border-[#FF541F]/30 rounded-full text-xs text-[#FF541F]">
                "{searchText}"
                <button onClick={() => setSearchText('')} className="ml-1 hover:opacity-70">✕</button>
              </span>
            )}
            {selectedDomain && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-500/15 border border-blue-500/30 rounded-full text-xs text-blue-400">
                {selectedDomain}
                <button onClick={() => setSelectedDomain('')} className="ml-1 hover:opacity-70">✕</button>
              </span>
            )}
            {(dateFrom || dateTo) && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-500/15 border border-green-500/30 rounded-full text-xs text-green-400">
                {dateFrom || '…'} → {dateTo || '…'}
                <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="ml-1 hover:opacity-70">✕</button>
              </span>
            )}
            <button
              onClick={() => { setSearchText(''); setSelectedDomain(''); setDateFrom(''); setDateTo(''); }}
              className="text-xs text-gray-500 hover:text-gray-300 ml-1 transition"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="flex justify-center items-center min-h-[400px]">
            <RippleWaveLoader messages={LOADING_MESSAGES} />
          </div>
        ) : useCases.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg mb-2">No use cases found</p>
            <p className="text-gray-600 text-sm">
              {searchText || selectedDomain
                ? 'Try adjusting your search or filters.'
                : 'Use cases saved from the discovery page will appear here.'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {useCases.map((useCase, idx) => (
                <motion.div
                  key={useCase.usecase_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: Math.min(idx % PAGE_SIZE, 8) * 0.05 }}
                  className={`relative text-white hover:scale-[1.02] transition-transform duration-200 ${
                    openSources === idx ? "z-50" : ""
                  }`}
                  style={{
                    background: "linear-gradient(0deg, rgba(39, 40, 41, 0.7), rgba(39, 40, 41, 0.7)),linear-gradient(119.3deg, rgba(0, 0, 0, 0) 23.34%, rgba(255, 60, 0, 0.76) 99.36%)",
                    borderRadius: 20,
                    padding: "24px",
                    border: "1px solid #FFFFFF1A",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    minHeight: "300px",
                  }}
                >
                  {/* Domain chip */}
                  {useCase.domain && (
                    <span
                      className="absolute top-3 right-3 z-10 text-xs font-semibold px-2.5 py-1 rounded-full"
                      style={{ background: "rgba(255,84,31,0.15)", color: "#FF541F", border: "1px solid rgba(255,84,31,0.25)" }}
                    >
                      {useCase.domain}
                    </span>
                  )}

                  {/* Title */}
                  <h2
                    className="text-xl font-semibold mb-3 mt-4 pr-24 leading-snug"
                    style={{ fontFamily: "IBM Plex Sans, sans-serif" }}
                  >
                    {useCase.title}
                  </h2>

                  {/* Description */}
                  <p
                    className="text-gray-300 mb-4 flex-grow"
                    style={{ fontFamily: "Lora, serif", fontSize: "15px", lineHeight: "1.5" }}
                  >
                    {useCase.description}
                  </p>

                  {/* Created date */}
                  {useCase.created_on && (
                    <p className="text-xs text-gray-600 mb-3">
                      Saved {formatDate(useCase.created_on)}
                    </p>
                  )}

                  {/* Source Button */}
                  {useCase.source && useCase.source.length > 0 && (
                    <div className="mb-2">
                      <div className="relative inline-block">
                        <button
                          type="button"
                          className="flex items-center font-semibold text-xs rounded-lg px-3 py-2 transition-colors gap-2"
                          style={{ background: "#2A2A2A", color: "#F3F4F6" }}
                          onClick={() => setOpenSources(openSources === idx ? null : idx)}
                        >
                          <span>Source ({useCase.source.length})</span>
                          <ChevronDownIcon className={`w-4 h-4 transition-transform ${openSources === idx ? "rotate-180" : ""}`} />
                        </button>

                        {openSources === idx && (
                          <div
                            className="absolute left-0 mt-2 w-[min(480px,90vw)] z-50"
                            style={{
                              background: "rgba(18,18,20,0.95)",
                              border: "1px solid rgba(67,72,84,0.6)",
                              borderRadius: 12, padding: 10,
                              boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                              backdropFilter: "blur(12px)",
                            }}
                          >
                            <ul className="flex flex-col gap-2">
                              {useCase.source.map((src, sidx) => {
                                let domain = src;
                                let displayTitle = src;
                                try {
                                  const urlObj = new URL(src);
                                  domain = urlObj.hostname.replace(/^www\./i, "");
                                  displayTitle = domain;
                                } catch (_e) {}

                                return (
                                  <li key={sidx}>
                                    <a
                                      href={src}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-white/5 transition-all border border-white/5 hover:border-orange-500/30 group"
                                    >
                                      <img
                                        src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`}
                                        alt="" className="w-8 h-8 rounded flex-shrink-0 mt-0.5"
                                        style={{ filter: "grayscale(100%) opacity(0.95)" }}
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="font-medium text-white group-hover:text-orange-400 transition-colors mb-1">
                                          {displayTitle}
                                        </div>
                                        <div className="text-xs text-neutral-500 break-words leading-relaxed" style={{ wordBreak: 'break-all' }}>
                                          {src}
                                        </div>
                                      </div>
                                      <svg className="w-4 h-4 flex-shrink-0 text-neutral-400 group-hover:text-orange-400 transition-colors mt-1"
                                        fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                      </svg>
                                    </a>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* View Details Button */}
                  <div style={{ position: "absolute", bottom: 24, right: 24 }}>
                    <button
                      onClick={() => handleViewDetails(useCase)}
                      className="flex items-center justify-center"
                      style={{
                        width: 48, height: 48, borderRadius: "50%",
                        background: "linear-gradient(119.3deg, #FF541F 23.34%, #FF3C00 96.36%)",
                        boxShadow: "0 2px 8px rgba(255, 60, 0, 0.15)",
                      }}
                    >
                      <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path d="M7 17L17 7M17 7H9M17 7V15" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Load More */}
            {currentPage < totalPages && (
              <div className="flex justify-center mt-10">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="px-8 py-3 rounded-full font-semibold text-white transition-all duration-200 hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                  style={{
                    background: "linear-gradient(119.3deg, #FF541F 23.34%, #FF3C00 96.36%)",
                    boxShadow: "0 4px 16px rgba(255, 60, 0, 0.25)",
                  }}
                >
                  {loadingMore ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Loading…</>
                  ) : (
                    <>Load More · {Math.min(PAGE_SIZE, totalCount - useCases.length)} more</>
                  )}
                </button>
              </div>
            )}

            {/* Results count */}
            {totalCount > 0 && (
              <p className="text-center text-xs text-gray-600 mt-6">
                Showing {useCases.length} of {totalCount} use cases
              </p>
            )}
          </>
        )}
      </main>

      <div className="mt-12 relative z-10">
        <Footer />
      </div>
    </div>
  );
};

export default LatestUseCases;
